import {
    login as loginApi,
    register as registerApi,
    logout as logoutApi,
    verifySession,
} from "../api/auth.api";

// NOTE: createContext, useState, useEffect, useCallback are auto-imported by unplugin-auto-import

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [customer, setCustomer] = useState({});
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Verify session on mount
    useEffect(() => {
        const checkSession = async () => {
            try {
                const response = await verifySession();
                const result = response.data;
                if (result.isOk && result.data?.authenticated) {
                    setCustomer(result.data.customer);
                    setIsAuthenticated(true);
                } else {
                    setCustomer(null);
                    setIsAuthenticated(false);
                }
            } catch {
                setCustomer(null);
                setIsAuthenticated(false);
            } finally {
                setLoading(false);
            }
        };

        checkSession();
    }, []);

    const login = useCallback(async (email, password) => {
        const response = await loginApi({ email, password });
        const result = response.data;
        if (result.isOk) {
            setCustomer(result.data);
            setIsAuthenticated(true);
        }
        return result;
    }, []);

    const register = useCallback(async (data) => {
        const response = await registerApi(data);
        const result = response.data;
        if (result.isOk) {
            setCustomer(result.data);
            setIsAuthenticated(true);
        }
        return result;
    }, []);

    const logout = useCallback(async () => {
        try {
            await logoutApi();
        } finally {
            setCustomer(null);
            setIsAuthenticated(false);
        }
    }, []);

    const updateCustomerData = useCallback((data) => {
        setCustomer(data);
    }, []);

    const value = useMemo(
        () => ({
            customer,
            loading,
            isAuthenticated,
            login,
            register,
            logout,
            updateCustomerData,
        }),
        [customer, loading, isAuthenticated, login, register, logout, updateCustomerData]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};

export { AuthContext };
export default AuthContext;
